import { LayoutGrid, List, Users, Plus, MessageSquare, MessageCircle, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { can, useMyCapabilities } from '@/hooks/use-permissions';
import type { ViewId, TaskViewId, ChatViewId } from './ViewRegistry';
import type { ProjectChat } from '@/types';
import type { TaskVisibilityScope } from '@/lib/api';

const VIEW_ICONS: Record<TaskViewId, typeof LayoutGrid> = {
  board: LayoutGrid,
  list: List,
  workload: Users,
};

const CHAT_ICONS: Record<ChatViewId, typeof MessageSquare> = {
  'internal-chat': MessageSquare,
  'client-chat': MessageCircle,
};

const TASK_VISIBILITY_LABELS: Record<TaskVisibilityScope, string> = {
  my: 'My tasks',
  team: 'Team tasks',
  all: 'All active tasks',
};

/** Solid filled triggers — distinct per scope (Button merges className via `cn(variants, className)`). */
const TASK_VISIBILITY_TRIGGER_CLASS: Record<TaskVisibilityScope, string> = {
  all:
    'border-slate-600 bg-slate-700 text-white hover:bg-slate-600 hover:text-white focus-visible:ring-slate-400',
  my: 'border-blue-500 bg-blue-600 text-white hover:bg-blue-500 hover:text-white focus-visible:ring-blue-400',
  team:
    'border-purple-500 bg-purple-600 text-white hover:bg-purple-500 hover:text-white focus-visible:ring-purple-400',
};

interface ViewBarProps {
  currentView: ViewId;
  onViewChange: (viewId: ViewId) => void;
  views: Array<{ id: TaskViewId; label: string }>;
  taskVisibilityScope: TaskVisibilityScope;
  onTaskVisibilityScopeChange: (scope: TaskVisibilityScope) => void;
  showTaskVisibilityFilter: boolean;
  chats?: ProjectChat[];
  isClient?: boolean;
}

export function ViewBar({
  currentView,
  onViewChange,
  views,
  taskVisibilityScope,
  onTaskVisibilityScopeChange,
  showTaskVisibilityFilter,
  chats = [],
  isClient = false,
}: ViewBarProps) {
  const { data: capabilities, isLoading: capabilitiesLoading } = useMyCapabilities();
  const canViewProjectChats = can(capabilities, 'chat.project.view');

  // Filter chats based on user role
  const visibleChats = chats.filter((chat) => {
    if (isClient) {
      // Clients can only see client chat
      return chat.type === 'client';
    }
    // For internal users, project chat tabs (internal/client) require chat.project.view.
    if (capabilitiesLoading || !canViewProjectChats) {
      return false;
    }
    return chat.type === 'internal' || chat.type === 'client';
  });

  return (
    <div className="flex items-center gap-1 border-b px-4 py-2">
      {/* Task views */}
      <div className="flex items-center gap-1">
        {views.map((view) => {
          const Icon = VIEW_ICONS[view.id];
          const isActive = currentView === view.id;

          return (
            <TooltipProvider key={view.id}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant={isActive ? 'secondary' : 'ghost'}
                    size="sm"
                    onClick={() => onViewChange(view.id)}
                    className={cn(
                      'gap-2 h-8 px-3',
                      isActive && 'bg-primary/10 text-primary hover:bg-primary/20'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    <span className="hidden sm:inline">{view.label}</span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{view.label} View</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>

      {showTaskVisibilityFilter && (
        <div className="ml-2 flex items-center gap-1.5">
          <span className="shrink-0 text-xs text-muted-foreground">Filter on</span>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className={cn(
                  'h-8 gap-1 border px-2.5 text-xs font-medium shadow-sm transition-colors',
                  TASK_VISIBILITY_TRIGGER_CLASS[taskVisibilityScope],
                )}
              >
                <span className="max-w-[10rem] truncate sm:max-w-[14rem]">
                  {TASK_VISIBILITY_LABELS[taskVisibilityScope]}
                </span>
                <ChevronDown className="h-3.5 w-3.5 shrink-0 opacity-90 text-white" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={() => onTaskVisibilityScopeChange('my')}>
                {TASK_VISIBILITY_LABELS.my}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTaskVisibilityScopeChange('team')}>
                {TASK_VISIBILITY_LABELS.team}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onTaskVisibilityScopeChange('all')}>
                {TASK_VISIBILITY_LABELS.all}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}

      {/* Divider between task views and chat views */}
      {visibleChats.length > 0 && <div className="ml-2 h-4 w-px bg-border" />}

      {/* Chat views */}
      {visibleChats.length > 0 && (
        <div className="flex items-center gap-1">
          {visibleChats.map((chat) => {
            const viewId: ChatViewId = chat.type === 'internal' ? 'internal-chat' : 'client-chat';
            const Icon = CHAT_ICONS[viewId];
            const isActive = currentView === viewId;
            const label = chat.type === 'internal' ? 'Internal Chat' : 'Client Chat';

            return (
              <TooltipProvider key={chat.id}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={isActive ? 'secondary' : 'ghost'}
                      size="sm"
                      onClick={() => onViewChange(viewId)}
                      className={cn(
                        'gap-2 h-8 px-3',
                        isActive && 'bg-primary/10 text-primary hover:bg-primary/20'
                      )}
                    >
                      <Icon className="h-4 w-4" />
                      <span className="hidden sm:inline">{label}</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{label}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          })}
        </div>
      )}

      <div className="ml-2 h-4 w-px bg-border" />

      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-muted-foreground hover:text-foreground"
              disabled
            >
              <Plus className="h-4 w-4" />
              <span className="ml-1 hidden sm:inline text-xs">View</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>More views coming soon</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}
